import { endOfDay, getUnixTime, startOfDay } from 'date-fns';
import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { formatYearMonthDay } from 'src/utils/dateFormatter';
import { decamelize } from 'src/utils/decamelize';

import { normalize } from '../shared/normalizers';

import {
  InventoryAdjustment,
  Product,
  ProductFile,
  ProductVariant,
} from './_types';
import {
  denormalizeInventoryAdjustment,
  denormalizeProduct,
  normalizeInventoryAdjustment,
  normalizeProduct,
  normalizeProductVariant,
} from './normalizers';

interface FetchProductOptions {
  id: Product['id'];
}

export const fetchProduct = async ({
  id,
}: FetchProductOptions): Promise<Product> => {
  const response = await api.get(`/api/v2/products/${id}`);
  return normalizeProduct(response.data);
};

interface FetchProductVariantOptions {
  id: ProductVariant['id'];
}

export const fetchProductVariant = async ({
  id,
}: FetchProductVariantOptions): Promise<ProductVariant> => {
  const response = await api.get(`/api/v4/product_variants/${id}`);
  return normalizeProductVariant(response.data);
};

export const fetchProductOptions = async (
  trainerId?: string
): Promise<Product[]> => {
  const response = await api.get('/api/v3/products', {
    params: {
      fetch_all: true,
      pluck_columns: 'id,name',
      'q[s]': 'name asc',
      trainer_id: trainerId,
    },
  });

  return response.data;
};

export const fetchProductsByParams = async (
  params = {}
): Promise<Product[]> => {
  const response = await api.get('/api/v3/products', {
    params: { ...params },
  });
  return response.data.map(normalizeProduct);
};

interface FetchProductsOptions {
  page?: number;
  perPage?: number;
  search?: string;
  tags?: { values: string[] };
  sortBy?: string;
  sortDir?: string;
  publishingChannel?: string;
}

export const fetchProductsQueryKey = (o: FetchProductsOptions) => [
  'products',
  o.page,
  o.perPage,
  o.search,
  o.tags,
  o.sortBy,
  o.sortDir,
  o.publishingChannel,
];

export const fetchProducts = async ({
  page = 1,
  perPage = 20,
  search = '',
  tags = { values: [] },
  sortBy,
  sortDir,
  publishingChannel,
}: {
  page?: number;
  perPage?: number;
  search?: string;
  tags?: { values: string[] };
  sortBy?: string;
  sortDir?: string;
  publishingChannel?: string;
}): Promise<{ products: Product[]; total: number }> => {
  const response = await api.get('/api/v3/products', {
    params: {
      page,
      per: perPage,
      ...(publishingChannel && {
        'q[has_publishing_channel]': publishingChannel,
      }),
      ...(search && { 'q[name_cont]': search }),
      ...(tags &&
        tags.values.length > 0 && { 'q[has_tags]': tags.values.join(',') }),
      ...(sortBy
        ? {
            'q[s]': `${decamelize(sortBy || '')} ${sortDir || 'desc'}`,
          }
        : { 'q[s]': 'updated_at desc' }),
    },
  });
  const normalizedProducts = response.data?.product?.map((product) =>
    normalizeProduct(product)
  );

  return { products: normalizedProducts, total: response.data?.meta?.total };
};

export const deleteProduct = async (productId: Identifier) => {
  try {
    const response = await api.delete(`/api/v3/products/${productId}`);

    return normalizeProduct(response.data);
  } catch (error: any) {
    throw new Error(api.formatError(error, 'Error deleting product.'));
  }
};

export const updateProduct = async (product: Partial<Product>) => {
  const response = await api.put(`/api/v3/products/${product.id}`, {
    product: {
      ...denormalizeProduct(product),
      tags: product.tags?.join(',') || '',
    },
  });

  return normalizeProduct(response.data);
};

export const createProduct = async (product: Partial<Product>) => {
  const response = await api.post('/api/v3/products', {
    product: {
      ...denormalizeProduct(product),
      tags: product.tags?.join(',') || '',
    },
  });
  return normalizeProduct(response.data);
};

export const handleProductImageUpload = async (file: File) => {
  const formData = new FormData();
  formData.append('asset_image[image]', file);
  const response = await api.post('/api/v2/assets/image/product', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const handleProductFileUpload = async (
  file: File
): Promise<ProductFile> => {
  const formData = new FormData();
  formData.append('product[file]', file);
  const response = await api.post('/api/v2/products/add_file', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return normalize(response.data);
};

export const handleProductFileDelete = async (id: Identifier) => {
  const formData = new FormData();
  formData.append('id', id as string);
  const response = await api.post('/api/v2/products/remove_file', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return normalize(response.data);
};

// Shared publishing channels for products and variants
export const publishingChannels = [
  { label: 'POS', value: 'pos' },
  { label: 'Staff', value: 'staff' },
  { label: 'Public Checkout / Finders', value: 'checkout' },
  { label: 'Self-Serve Kiosk', value: 'kiosk' },
];

interface FetchProductVariantsOptions {
  page?: number;
  perPage?: number;
  search?: string;
  productId?: string;
  location?: string;
  category?: string;
  tags?: { values: string[] };
  stockStatus?: string;
  sortBy?: string;
  sortDir?: string;
  fetchAll?: boolean;
  productIds?: string[];
  // Allow product option filters with pattern: productOption_OptionName
  [key: `productOption_${string}`]: string | undefined;
}

export const fetchProductVariantsQueryKey = (
  o: FetchProductVariantsOptions & Record<string, any>
) => {
  const baseKey = [
    'product_variants',
    o.page,
    o.perPage,
    o.search,
    o.productId,
    o.location,
    o.category,
    o.tags,
    o.stockStatus,
    o.sortBy,
    o.sortDir,
    o.fetchAll,
    o.productIds,
  ];

  // Add product option filters to the query key
  const productOptionEntries = Object.entries(o)
    .filter(([key, value]) => key.startsWith('productOption_') && value)
    .sort(([a], [b]) => a.localeCompare(b)); // Sort for consistent ordering

  return [...baseKey, ...productOptionEntries.flat()];
};

export const fetchProductVariants = async ({
  page = 1,
  perPage = 20,
  search = '',
  productId,
  location,
  category,
  stockStatus,
  sortBy,
  sortDir,
  fetchAll,
  ...additionalOptions
}: FetchProductVariantsOptions & Record<string, any>): Promise<{
  productVariants: Array<ProductVariant>;
  total: number;
}> => {
  const params: any = {
    ...(fetchAll ? { 'q[fetch_all]': true } : { page, per: perPage }),
    ...(productId && { 'q[has_product]': productId }),
    ...(search && { 'q[search]': search }),
    ...(location && { 'q[has_location]': location }),
    ...(category && {
      'q[product_tags_name_eq_any]': Array.isArray(category)
        ? category
        : category.split(','),
    }),
    ...(stockStatus && { 'q[stock_status]': stockStatus }),
    ...(sortBy
      ? {
          'q[s]': `${decamelize(sortBy || '')} ${sortDir || 'desc'}`,
        }
      : { 'q[s]': 'updated_at desc' }),
  };

  if (productId) {
    // Handle product option filters
    const productOptionFilters: string[] = [];
    Object.keys(additionalOptions).forEach((key) => {
      if (key.startsWith('productOption_') && additionalOptions[key]) {
        const optionName = key.replace('productOption_', '');
        const optionValue = additionalOptions[key];
        productOptionFilters.push(`${optionName}##${optionValue}`);
      }
    });

    // Send all product option filters as array if any exist
    if (productOptionFilters.length > 0) {
      params['q[has_option_value]'] = productOptionFilters.join('###');
    }
  }

  const response = await api.get('/api/v4/product_variants', { params });
  // The API returns product_variants as root or as an array
  const productVariants = response.data.product_variants || response.data;
  const total = response.data.meta?.total || productVariants.length || 0;
  // No further normalization needed unless you want camelCase keys
  return {
    productVariants: productVariants.map((variant) =>
      normalizeProductVariant(variant)
    ),
    total,
  };
};

interface CreateInventoryAdjustmentOptions {
  productVariantId: Identifier;
  locationId: Identifier;
  adjustmentAmount: number;
  reason: string;
  note?: string;
}

interface FetchInventoryAdjustmentsOptions {
  page?: number;
  perPage?: number;
  search?: string;
  productVariantId?: Identifier;
  locationId?: Identifier;
  dateRange?: {
    start?: Date;
    end?: Date;
  };
  productTags?: string[];
  adjustmentTypes?: string[];
  productIds?: string;
  sortBy?: string;
  sortDir?: string;
}

export const fetchInventoryAdjustmentsQueryKey = (
  o: FetchInventoryAdjustmentsOptions
) => [
  'inventory_adjustments',
  o.page,
  o.perPage,
  o.search,
  o.productVariantId,
  o.locationId,
  o.dateRange,
  o.productTags,
  o.adjustmentTypes,
  o.productIds,
  o.sortBy,
  o.sortDir,
];

export const fetchInventoryAdjustments = async ({
  page = 1,
  perPage = 20,
  search = '',
  productVariantId,
  locationId,
  dateRange,
  productTags = [],
  adjustmentTypes = [],
  productIds,
  sortBy,
  sortDir,
}: FetchInventoryAdjustmentsOptions): Promise<{
  inventoryAdjustments: Array<InventoryAdjustment>;
  total: number;
}> => {
  const params: any = {
    page,
    per: perPage,
    ...(search && {
      'q[product_variant_sku_or_product_name_or_product_variant_barcode_cont]':
        search,
    }),
    ...(productVariantId && { 'q[has_product_variant]': productVariantId }),
    ...(locationId && { 'q[location_id_eq]': locationId }),
    ...(dateRange?.start && {
      start_date: getUnixTime(startOfDay(new Date(dateRange.start))),
    }),
    ...(dateRange?.end && {
      end_date: getUnixTime(endOfDay(new Date(dateRange.end))),
    }),
    ...(productTags.length > 0 && {
      'q[has_tags]': productTags.join(','),
    }),
    ...((typeof productIds === 'string' || typeof productIds === 'number') && {
      'q[product_id_in]': String(productIds).split(','),
    }),
    ...(adjustmentTypes.length > 0 && {
      'q[has_adjustment_type]': adjustmentTypes.join(','),
    }),
    ...(sortBy
      ? {
          'q[s]': `${decamelize(sortBy || '')} ${sortDir || 'desc'}`,
        }
      : { 'q[s]': 'created_at desc' }),
  };

  const response = await api.get('/api/v4/inventory_adjustments', { params });
  const inventoryAdjustments =
    response.data.inventory_adjustments || response.data;
  const total = response.data.meta?.total || inventoryAdjustments.length || 0;

  return {
    inventoryAdjustments: inventoryAdjustments.map((adjustment) =>
      normalizeInventoryAdjustment(adjustment)
    ),
    total,
  };
};

export const createInventoryAdjustment = async ({
  productVariantId,
  locationId,
  adjustmentAmount,
  reason,
  note,
}: CreateInventoryAdjustmentOptions): Promise<InventoryAdjustment> => {
  const adjustmentData = denormalizeInventoryAdjustment({
    productVariantId,
    locationId,
    reason,
    note,
  });

  const response = await api.post('/api/v4/inventory_adjustments', {
    inventory_adjustment: {
      ...adjustmentData,
      quantity_delta: adjustmentAmount,
    },
  });

  return normalizeInventoryAdjustment(response.data);
};

export const fetchInventoryAdjustmentByCartItemId = async (
  cartItemId: Identifier
): Promise<InventoryAdjustment | null> => {
  try {
    const response = await api.get('/api/v4/inventory_adjustments', {
      params: {
        'q[cart_item_id_eq]': cartItemId,
        per: 1, // We only expect one adjustment per cart item
      },
    });

    const adjustments = response.data.inventory_adjustments || response.data;
    if (adjustments && adjustments.length > 0) {
      return normalizeInventoryAdjustment(adjustments[0]);
    }
    return null;
  } catch (error: any) {
    return null;
  }
};

export const exportInventoryAdjustments = async ({
  productVariantId,
  locationId,
  dateRange,
  productTags = [],
  adjustmentTypes = [],
  productIds,
}: FetchInventoryAdjustmentsOptions): Promise<void> => {
  return await api.get('/api/v4/reports/inventory_adjustments.csv', {
    params: {
      ...(productVariantId && { product_variant_id: productVariantId }),
      ...(locationId && { location_id: locationId }),
      ...(dateRange?.start && {
        start_date_string: formatYearMonthDay(new Date(dateRange.start)),
      }),
      ...(dateRange?.end && {
        end_date_string: formatYearMonthDay(new Date(dateRange.end)),
      }),
      ...(productTags.length > 0 && {
        product_tag: productTags.join(','),
      }),
      ...(typeof productIds === 'string' && {
        product_id: productIds,
      }),
      ...(adjustmentTypes.length > 0 && {
        adjustment_type: adjustmentTypes.join(','),
      }),
    },
  });
};
